import {
  type CV,
  type CVStorageAdapter,
  type CVSummary,
  type CreateCVRequest,
  type PDFOptions,
  type UpdateCVRequest,
  createEmptyCV,
  renderCVDocument,
} from '@cv-builder/core'

export interface VHApiConfig {
  /** Base URL for the API */
  baseUrl: string
  /** Function to get the current auth token */
  getToken: () => Promise<string | null>
  /**
   * Resolve the current hive id. Sent as the `virtualhive-tenant` header, which
   * api-service `/cvs` uses for hive scoping (the user is derived from the token,
   * NOT from a query param — see CVResource). Returns null when unknown.
   */
  getHiveId: () => Promise<string | null>
  /**
   * Base URL of the pdf-service (same-origin proxy, e.g. '/pdf'). PDFs are
   * rendered with the SHARED core renderer and sent here — identical to the
   * on-screen preview — instead of the api-service Java renderer, which drifts.
   */
  pdfServiceUrl: string
}

/**
 * api-service `CVDto`. The CV's section fields live inside an opaque `content`
 * JSONB blob; the core `CV` keeps them flat. These two mappers bridge that.
 */
interface CVDto {
  id: string
  title: string
  templateId: string
  locale: string
  content?: Partial<CVContent>
  isPublished?: boolean
  publishedAt?: string | null
  hiveId?: string
  loginId?: string
  created?: string
  updated?: string
  deleted?: string | null
}

type CVContent = Pick<
  CV,
  | 'personalInfo'
  | 'experiences'
  | 'education'
  | 'skills'
  | 'languages'
  | 'certifications'
  | 'customSections'
>

const CONTENT_KEYS: (keyof CVContent)[] = [
  'personalInfo',
  'experiences',
  'education',
  'skills',
  'languages',
  'certifications',
  'customSections',
]

/** Pick just the CV section fields into the opaque `content` blob the backend stores. */
function toContent(source: Partial<Record<keyof CVContent, unknown>>): Record<string, unknown> {
  const content: Record<string, unknown> = {}
  for (const key of CONTENT_KEYS) {
    if (source[key] !== undefined) {
      content[key] = source[key]
    }
  }
  return content
}

/** Backend DTO -> flat core CV (start from an empty CV so every field is present). */
function dtoToCV(dto: CVDto): CV {
  const base = createEmptyCV(dto.id)
  return {
    ...base,
    id: dto.id,
    title: dto.title ?? base.title,
    templateId: dto.templateId ?? base.templateId,
    locale: dto.locale ?? base.locale,
    createdAt: dto.created ?? base.createdAt,
    updatedAt: dto.updated ?? base.updatedAt,
    ...(dto.content ?? {}),
  }
}

function dtoToSummary(dto: CVDto): CVSummary {
  return {
    id: dto.id,
    title: dto.title,
    templateId: dto.templateId,
    updatedAt: dto.updated ?? '',
  }
}

/**
 * Virtual Hive API storage adapter
 * Communicates with the Quarkus api-service `/cvs` endpoints.
 */
export function createVHStorageAdapter(config: VHApiConfig): CVStorageAdapter {
  const { baseUrl, getToken, getHiveId, pdfServiceUrl } = config

  const getHeaders = async (): Promise<Headers> => {
    const [token, hiveId] = await Promise.all([getToken(), getHiveId()])
    const headers = new Headers({
      'Content-Type': 'application/json',
    })
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    if (hiveId) {
      headers.set('virtualhive-tenant', hiveId)
    }
    return headers
  }

  const handleResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new Error(`API Error (${response.status}): ${errorText}`)
    }
    return response.json()
  }

  return {
    async listCVs(): Promise<CVSummary[]> {
      const response = await fetch(`${baseUrl}/cvs`, {
        method: 'GET',
        headers: await getHeaders(),
      })
      const dtos = await handleResponse<CVDto[]>(response)
      return dtos.map(dtoToSummary)
    },

    async getCV(id: string): Promise<CV> {
      const response = await fetch(`${baseUrl}/cvs/${id}`, {
        method: 'GET',
        headers: await getHeaders(),
      })
      return dtoToCV(await handleResponse<CVDto>(response))
    },

    async createCV(data?: CreateCVRequest): Promise<CV> {
      const response = await fetch(`${baseUrl}/cvs`, {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify({
          title: data?.title,
          templateId: data?.templateId,
          locale: data?.locale,
        }),
      })
      return dtoToCV(await handleResponse<CVDto>(response))
    },

    async updateCV(id: string, data: UpdateCVRequest): Promise<CV> {
      const response = await fetch(`${baseUrl}/cvs/${id}`, {
        method: 'PUT',
        headers: await getHeaders(),
        body: JSON.stringify({
          title: data.title,
          templateId: data.templateId,
          locale: data.locale,
          content: toContent(data),
        }),
      })
      return dtoToCV(await handleResponse<CVDto>(response))
    },

    async deleteCV(id: string): Promise<void> {
      const response = await fetch(`${baseUrl}/cvs/${id}`, {
        method: 'DELETE',
        headers: await getHeaders(),
      })
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error')
        throw new Error(`API Error (${response.status}): ${errorText}`)
      }
    },

    async generatePDF(id: string, options?: PDFOptions): Promise<Blob> {
      // Render with the SHARED core renderer (identical to the on-screen preview)
      // and hand the HTML to pdf-service — the same path the demo uses — so the
      // PDF matches the preview. This bypasses the api-service Java CVHtmlRenderer,
      // which produces different styling (rendering drift).
      const cvResponse = await fetch(`${baseUrl}/cvs/${id}`, {
        method: 'GET',
        headers: await getHeaders(),
      })
      const cv = dtoToCV(await handleResponse<CVDto>(cvResponse))
      const html = renderCVDocument(cv)

      const margins = options?.margins ?? { top: 15, right: 20, bottom: 15, left: 20 }
      const fallbackSideMargin = margins.sides ?? 20
      const response = await fetch(`${pdfServiceUrl}/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html,
          options: {
            format: 'A4',
            printBackground: true,
            margin: {
              top: `${margins.top}mm`,
              right: `${margins.right ?? fallbackSideMargin}mm`,
              bottom: `${margins.bottom}mm`,
              left: `${margins.left ?? fallbackSideMargin}mm`,
            },
          },
        }),
      })
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error')
        throw new Error(`PDF Generation Error (${response.status}): ${errorText}`)
      }
      return response.blob()
    },

    async uploadPhoto(file: File): Promise<string> {
      // api-service has no CV photo endpoint yet (Phase 2 — will be
      // POST /cvs/{id}/photo, reusing ResourceResource/ObjectStorageService).
      // Until then, store the photo inline as a base64 data: URL in the CV
      // content (same as adapter-local). This needs no backend endpoint, and the
      // pdf-service renderer explicitly allows data: URLs (its SSRF guard blocks
      // only non-data: sub-resource fetches), so the photo also renders in PDFs.
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result)
          } else {
            reject(new Error('Failed to read photo file'))
          }
        }
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read photo file'))
        reader.readAsDataURL(file)
      })
    },
  }
}
